import { useRef, useEffect, useState, Fragment } from "react";
import { Link, useLoaderData } from "react-router-dom";
import { Helmet } from "react-helmet";
import * as Dialog from "@radix-ui/react-dialog";
import * as Popover from "@radix-ui/react-popover";
import * as Tabs from "@radix-ui/react-tabs";
import * as Tooltip from "@radix-ui/react-tooltip";
import * as WolframNotebookEmbedder from "wolfram-notebook-embedder";
import SidebarTabs from "./sidebar-tabs";
import LoadingSpinner from "../../components/LoadingSpinner";
import {
  Desktop,
  NotDesktop,
  Mobile,
  NotMobile,
} from "../../components/responsive-components";

import "./demonstration-page.css";

function ClickToCopy({ text, children, initialLabel = "Copy to Clipboard" }) {
  const [tooltipLabel, setTooltipLabel] = useState(initialLabel);
  const triggerRef = useRef(null);
  const resetTimeoutRef = useRef(null);

  function scheduleTooltipReset() {
    if (resetTimeoutRef.current !== null) {
      clearTimeout(resetTimeoutRef.current);
    }
    resetTimeoutRef.current = setTimeout(() => {
      setTooltipLabel(initialLabel);
      resetTimeoutRef.current = null;
    }, 5000);
  }

  useEffect(() => {
    return () => {
      if (resetTimeoutRef.current !== null) {
        clearTimeout(resetTimeoutRef.current);
      }
    };
  }, []);

  function handleClick(e) {
    e.preventDefault();
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setTooltipLabel("Copied!");
        scheduleTooltipReset();
      })
      .catch(() => {
        setTooltipLabel("Copy failed");
        scheduleTooltipReset();
      });
  }

  return (
    <Tooltip.Provider delayDuration={0} disableHoverableContent={true}>
      <Tooltip.Root>
        <Tooltip.Trigger ref={triggerRef} onClick={handleClick} asChild>
          {children}
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            onPointerDownOutside={(event) => {
              if (triggerRef.current?.contains(event.target))
                event.preventDefault();
            }}
            className="TooltipContent"
            sideOffset={5}
          >
            {tooltipLabel}
            <Tooltip.Arrow className="TooltipArrow" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}

function DownloadButton(props) {
  return (
    <a
      className="buttonUnderExample"
      href={props.openExampleNotebookInCloudURL}
    >
      {props.label}
    </a>
  );
}

function ManipulateC2C(props) {
  return (
    <ClickToCopy
      text={props.clickToCopyText}
      initialLabel="Copy code to clipboard"
    >
      <button className="buttonUnderExample">{props.label}</button>
    </ClickToCopy>
  );
}
function PageTitle({ title }) {
  return <h1 id="demoTitle">{title}</h1>;
}
function ResourceObjectSection({ content }) {
  return (
    <div className="resource-object-subhead">
      <ClickToCopy text={content} initialLabel="Copy code to clipboard">
        <button className="resource-object-subhead-button">
          <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
            <title>Wolfram Language</title>
            <path
              d="M16,0C24.831,0 32,7.169 32,16C32,24.831 24.831,32 16,32C7.169,32 -0,24.831 -0,16C-0,7.169 7.169,0 16,0ZM23.706,28.282C27.786,25.716 30.5,21.172 30.5,16C30.5,7.997 24.003,1.5 16,1.5C7.997,1.5 1.5,7.997 1.5,16C1.5,18.3 2.037,20.475 2.991,22.408L3.065,22.217C4.564,18.127 6.469,13.334 9.084,10.435C10.226,7.041 11.825,4.134 13.911,2.414C14.607,4.667 14.933,5.445 15.629,7.694C17.224,6.218 17.43,5.974 19.025,4.378C19.025,4.341 19.561,9.29 19.314,13.015C21.482,15.223 27.825,19.233 27.825,19.233C27.825,19.233 25.616,22.346 25.208,22.507C24.758,22.714 22.792,22.507 22.792,22.507L18.7,22.057L23.706,28.282ZM4.534,24.873C7.187,28.295 11.338,30.5 16,30.5C17.808,30.5 19.54,30.168 21.137,29.562L16.408,21.693L14.937,21.651C14.895,21.651 14.854,21.651 14.772,21.61C11.945,21.238 8.182,21.813 8.182,21.813C8.425,19.605 9.694,18.336 9.694,18.336C9.694,18.336 8.878,17.968 7.732,17.682C7.979,16.781 10.353,15.718 10.679,15.594C10.514,15.797 9.863,16.533 9.616,17.108C10.477,17.232 11.623,17.926 11.623,17.926C11.623,17.926 10.395,19.075 9.822,20.506C11.046,20.303 12.031,20.221 13.053,20.303L24.837,21.49L25.941,20.097C25.941,20.097 25.575,18.951 25.286,18.786C22.545,17.393 18.453,14.036 15.753,11.336C15.139,9.62 14.24,6.061 13.667,4.341C11.376,6.061 11.009,9.331 9.739,11.543C7.31,13.366 5.803,19.519 4.58,24.687L4.534,24.873ZM16,8.922C16.165,9.451 16.408,10.886 16.408,10.886C16.408,10.886 17.636,11.667 18.25,12.279C18.374,9.782 18.415,8.719 18.333,6.673C17.269,7.694 17.105,7.859 16,8.922ZM17.554,17.025C16.775,16.575 16.082,16.327 15.427,16.409C15.506,16 15.629,15.715 15.753,15.347C17.142,15.144 18.168,15.88 19.108,16.575C18.374,16.781 18.292,16.822 17.554,17.025Z"
              fill="currentColor"
            />
          </svg>
          <code className="resource-object-subhead-text">{content}</code>
        </button>
      </ClickToCopy>
    </div>
  );
}
function DownloadSourceNotebookButton(props) {
  const [hideDownloadNotebookModal, setHideDownloadNotebookModal] =
    useState(false);
  useEffect(() => {
    const storedValue = localStorage.getItem("hideDownloadNotebookModal");
    if (storedValue) {
      setHideDownloadNotebookModal(storedValue === "true");
    }
  }, []);
  const handleChange = (event) => {
    const newValue = event.target.checked;
    setHideDownloadNotebookModal(newValue);
    localStorage.setItem("hideDownloadNotebookModal", newValue);
  };
  const downloadLink = (
    <a className="buttonUnderExample" href={props.url} download>
      {props.label}
    </a>
  );
  if (hideDownloadNotebookModal) {
    return downloadLink;
  }
  return (
    <Dialog.Root>
      <Dialog.Trigger asChild>{downloadLink}</Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="DialogOverlaySourceNotebook" />
        <Dialog.Content className="DialogContentSourceNotebook">
          <Dialog.Title>Requires a Wolfram Notebook System</Dialog.Title>
          <Dialog.Description>
            <p>
              Interact on desktop, mobile, and cloud with the free{" "}
              <a href="https://www.wolfram.com/player/">Wolfram Player</a>, or
              other{" "}
              <a href="https://www.wolfram.com/products/">
                Wolfram Language products
              </a>
              .
            </p>
            <p>
              <label>
                <input
                  type="checkbox"
                  checked={hideDownloadNotebookModal}
                  onChange={handleChange}
                />{" "}
                Do not show again
              </label>
            </p>
            <p>
              <a
                className="button orange"
                href="https://www.wolfram.com/player/"
              >
                Download Wolfram Player
              </a>
            </p>
          </Dialog.Description>
          <Dialog.Close asChild>
            <button className="IconButton" aria-label="Close">
              &times;
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
function SourceButton(props) {
  return (
    <Popover.Root>
      <Popover.Trigger className="buttonUnderExample Source">
        More
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="PopoverContent Source"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            event.target.focus();
          }}
          sideOffset={5}
        >
          {props.exampleNotebookButton && (
            <p>
              <DownloadButton
                label={props.exampleNotebookButton.Label}
                openExampleNotebookInCloudURL={props.exampleNotebookButton.URL}
              />
            </p>
          )}
          {props.copyManipulateButton && (
            <p>
              <ManipulateC2C
                label={props.copyManipulateButton.Label}
                clickToCopyText={props.copyManipulateButton.Content}
              />
            </p>
          )}
          <Popover.Arrow className="PopoverArrow" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
function ButtonsUnderDemonstration(props) {
  const pageData = props.pageData;
  const openFullScreenModal = props.openFullScreenModal;
  return (
    <div className="button-frame">
      <NotMobile>
        {pageData.ExampleNotebookButton && (
          <DownloadButton
            label={pageData.ExampleNotebookButton.Label}
            openExampleNotebookInCloudURL={pageData.ExampleNotebookButton.URL}
          />
        )}
        {pageData.CopyManipulateButton && (
          <ManipulateC2C
            label={pageData.CopyManipulateButton.Label}
            clickToCopyText={pageData.CopyManipulateButton.Content}
          />
        )}
        {pageData.SourceNotebookButton && (
          <DownloadSourceNotebookButton
            label={pageData.SourceNotebookButton.Label}
            url={pageData.SourceNotebookButton.URL}
          />
        )}
      </NotMobile>
      <Mobile>
        {pageData.SourceNotebookButton && (
          <DownloadSourceNotebookButton
            label={pageData.SourceNotebookButton.Label}
            url={pageData.SourceNotebookButton.URL}
          />
        )}
        {(pageData.ExampleNotebookButton || pageData.CopyManipulateButton) && (
          <SourceButton
            exampleNotebookButton={pageData.ExampleNotebookButton}
            copyManipulateButton={pageData.CopyManipulateButton}
          />
        )}
      </Mobile>
      {pageData.CloudCompatible && (
        <button
          onClick={() => {
            openFullScreenModal();
          }}
          className="buttonUnderExample FullScreen"
        >
          <svg viewBox="0 0 16 16" width="1em" height="1em">
            <title>Fullscreen Demonstration</title>
            <path
              d="M3.41 14H6v2H0v-6h2v2.59l3.3-3.3 1.4 1.42L3.42 14Zm9.18 0-3.3-3.3 1.42-1.4L14 12.58V10h2v6h-6v-2h2.59ZM14 3.41l-3.3 3.3-1.4-1.42L12.58 2H10V0h6v6h-2V3.41Zm-12 0V6H0V0h6v2H3.41l3.3 3.3-1.42 1.4L2 3.42Z"
              fill="#fff"
            />
          </svg>
        </button>
      )}
    </div>
  );
}

function LinkToAuthors(props) {
  return props.authors.map((author, index) => (
    <span key={author.Name}>
      {index ? ", " : ""}
      {author.Label} <Link to={"../authors/" + author.Slug}>{author.Name}</Link>
      {author.Suffix ? " " + author.Suffix : ""}
    </span>
  ));
}
function ListOfAuthors(props) {
  return props.authors.map((author, index) => (
    <span key={author.Name}>
      {index ? ", " : ""}
      {author.Label} {author.Name}
      {author.Suffix ? " " + author.Suffix : ""}
    </span>
  ));
}
function ContributedBySection(props) {
  return (
    <div id="contributed-section">
      <p className="contributed-by">
        {props.comment && (
          <Fragment>
            <span>{props.comment}</span>
            <br />
          </Fragment>
        )}
        {props.label} <LinkToAuthors authors={props.authors} /> {props.suffix}
        {props.date && <span id="publishdate">({props.date})</span>}
        <span className="contentTerms">
          <br />
          Open content licensed under{" "}
          <a href="http://www.wolfram.com/legal/terms/wolfram-demonstrations-project.html">
            CC BY-NC-SA
          </a>
        </span>
      </p>
    </div>
  );
}
function ExternalLink(props) {
  const url = new URL(props.url);
  let icon = (
    <svg viewBox="0 0 18 18" className="externalLinkIcon">
      <path d="m15 4.41-6.3 6.3a1 1 0 0 1-1.4-1.42L13.58 3H10a1 1 0 0 1-1-1 1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1 1 1 0 0 1-1-1V4.41ZM7 3a1 1 0 0 1 1 1 1 1 0 0 1-1 1H4v9h9v-3a1 1 0 0 1 1-1 1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h4Z" />
    </svg>
  );
  if (url.host.endsWith("wolfram.com")) {
    icon = (
      <svg viewBox="0 0 60 60" className="externalLinkIcon">
        <path d="m30 60-6.38-10.71-11.57 4.98L13.3 41.9.97 39.27 9.39 30 .97 20.7l12.33-2.64L12.05 5.7l11.57 5.01L30 0l6.38 10.71 11.57-4.98L46.7 18.1l12.33 2.64-8.4 9.27 8.4 9.27L46.7 41.9l1.25 12.36-11.57-5.02L30 60Zm-4.26-13.02 2.79 4.67v-4.7l-2.89-4.12.1 4.16Zm5.77 0v4.7l2.78-4.66.1-4.16-2.88 4.13Zm5.76-.53 7.18 3.09-.77-7.7-6.24-1.95-.16 6.56Zm-20.95-4.57-.77 7.69 7.18-3.09-.16-6.52-6.25 1.92Zm9.51-3.9L30 43.92l4.17-5.96L30 32.43l-4.17 5.54Zm15.26 0 4.04 1.25 5.5-1.2-4.54-1.54-5 1.48Zm-31.73.09 5.51 1.2L18.91 38l-5-1.45-4.55 1.52Zm23.08-7.38 4.16 5.55 7.05-2.05-4.48-5.73-6.73 2.23Zm-16.06 3.5 7.05 2.05 4.17-5.55-6.73-2.23-4.49 5.73Zm30.67-.44 4.26 1.42-3.56-3.9-3.71-1.36 3 3.84Zm-34.8-2.52-3.56 3.94 4.26-1.42 3.01-3.84-3.71 1.32Zm29.77-5.2 6.15 2.24 5.22-5.74-7.69-1.64-3.68 5.14ZM6.6 22.53l5.19 5.74 6.15-2.24-3.65-5.17-7.7 1.67Zm24.9-1.67v7.06l6.73-2.24.19-7.25-6.92 2.43Zm-9.71 4.82 6.73 2.24v-7.06l-6.93-2.43.2 7.25Zm19.67-8.85-.13 5.07 2.31-3.21.55-5.49-2.73 3.63Zm-25.09 1.86 2.3 3.21-.12-5.07-2.72-3.6.54 5.46Zm9.68-6.24L30 17.43l3.94-4.98L30 5.8l-3.94 6.65Zm-5.1 2.65 4.71 1.63-2.34-2.96-4.93-2.14 2.56 3.46Zm15.74-1.33-2.34 2.96 4.7-1.63 2.6-3.44-4.96 2.11Z" />
      </svg>
    );
  }
  if (url.host == "reference.wolfram.com") {
    icon = (
      <svg viewBox="0 0 32 32" className="externalLinkIcon">
        <path d="M16 0a16 16 0 1 1-.01 32.01A16 16 0 0 1 16 0Zm7.7 28.28A14.5 14.5 0 1 0 3 22.41l.06-.2c1.5-4.08 3.4-8.88 6.02-11.78 1.15-3.39 2.74-6.3 4.83-8.02.7 2.26 1.02 3.04 1.72 5.28 1.6-1.47 1.8-1.72 3.4-3.31 0-.04.53 4.91.28 8.64 2.17 2.2 8.52 6.21 8.52 6.21s-2.21 3.12-2.62 3.28c-.45.2-2.42 0-2.42 0l-4.09-.45 5 6.22Zm-19.17-3.4a14.48 14.48 0 0 0 16.6 4.68l-4.72-7.87-1.47-.04c-.04 0-.09 0-.17-.04-2.82-.37-6.59.2-6.59.2.25-2.2 1.51-3.47 1.51-3.47s-.81-.37-1.96-.66c.25-.9 2.62-1.96 2.95-2.09-.17.2-.82.94-1.06 1.52.86.12 2 .82 2 .82s-1.22 1.14-1.8 2.58c1.23-.2 2.21-.29 3.23-.2l11.79 1.18 1.1-1.4s-.37-1.14-.65-1.3c-2.74-1.4-6.84-4.75-9.54-7.45-.61-1.72-1.51-5.28-2.08-7-2.3 1.72-2.66 5-3.93 7.2C7.3 13.37 5.8 19.52 4.58 24.7l-.05.18ZM16 8.91c.16.53.4 1.97.4 1.97s1.24.78 1.85 1.39c.12-2.5.16-3.56.08-5.6L16 8.91Zm1.55 8.1c-.78-.45-1.47-.7-2.12-.61.08-.41.2-.7.32-1.06 1.4-.2 2.42.53 3.36 1.22-.74.21-.82.25-1.56.45Z" />
      </svg>
    );
  }
  if (url.host == "www.wolframscience.com") {
    icon = (
      <svg viewBox="0 0 18 18" className="externalLinkIcon">
        <path d="M3 17H1v-1h1v-1h1v-1h1v-1h1v-1h1v-1h1v-1h1V9h1V8h1V7h1V6h1V5h1V4h1V3h1V2h1V1h1v16h-1v-1h-1v1h-5v-1H8v1H6v-1h1v-1h1v-1H5v3H4v-1H3v1Zm4-5v1h1v-1H7Zm2-2v5h1v-1h1v-1h1v-1h1v-1h1v-1H9Zm2-2v1h1V8h-1Zm2-2v3h1V8h1V7h1V6h-3Zm2-2v1h1V4h-1Zm1 10v-1h-3v3h1v-1h1v-1h1Zm0-2v-1h-1v1h1Zm0-2V9h-1v1h1Zm-4 6v-1h-1v1h1Z" />
      </svg>
    );
  }
  if (
    url.host == "mathworld.wolfram.com" ||
    url.host == "scienceworld.wolfram.com"
  ) {
    icon = (
      <svg viewBox="0 0 21 21" className="externalLinkIcon">
        <path d="M10.5 0a10.5 10.5 0 1 0 0 21 10.5 10.5 0 0 0 0-21ZM3.6 15.6c0-.5.1-1 .2-1.6 1.1.9 2.3 1.7 3.7 2.4l-.6 1.4c-1.2-.6-2.4-1.4-3.3-2.2Zm3.1 2.6-.2.5c-.9-.4-1.8-1-2.5-1.6-.2-.2-.3-.5-.3-.8.8.8 1.9 1.4 3 1.9ZM3 15.1a5.6 5.6 0 0 1-1.6-2.4c-.1-.5-.1-1.1-.1-1.6.4.8 1.1 1.7 2 2.5-.1.5-.2 1.1-.3 1.5Zm.1 1.2c-.3-.4-.6-.8-.8-1.2l.7.7c.1.2.1.3.1.5ZM1.3 9.2V9c.1-.7.4-1.5.7-2.2 0-.1 0-.1.1-.2 0 .3.1.6.2.9-.3.4-.5.8-.7 1.2-.2.1-.2.3-.3.5Zm.2 1c.1-.4.2-.8.4-1.2.1-.3.3-.6.5-.9.4.9 1.1 1.9 2.1 2.8l-.6 1.2c-.1.3-.3.6-.4.9-1-.9-1.6-1.9-2-2.8Zm17.1-3.3c-.1.4-.3.8-.8 1-.3-.8-.8-1.6-1.3-2.4.2-.1.3-.3.4-.5.7.7 1.3 1.3 1.7 1.9Zm-1.5-2.8c.4.2.7.5 1 .8.3.4.4.7.5 1V6c-.4-.5-.9-1-1.4-1.5-.1-.1-.1-.2-.1-.4Zm2.6 5.4c-.1.6-.5 1.1-1.1 1.5-.1-.8-.3-1.7-.6-2.5.4-.2.7-.6.9-.9.4.6.7 1.2.8 1.9Zm-.5-2.6ZM7.1 8.2c.6-.7 1.2-1.3 1.8-1.9.7.6 1.5 1.1 2.5 1.6l-1.1 2.4C9.1 9.7 8 9 7.1 8.2Zm3 2.6L9 13.2c-1.4-.7-2.7-1.5-3.7-2.4.4-.7.9-1.4 1.5-2.1.9.8 2.1 1.5 3.3 2.1Zm1.8-2.7c.8.3 1.5.6 2.3.7.2 0 .4.1.6.1 0 .9-.1 1.8-.2 2.6-.3 0-.6-.1-.9-.1-1-.2-2-.5-3-.9.5-.8.9-1.6 1.2-2.4Zm2.7-2.2.3 2.4c-.2 0-.4-.1-.5-.1-.7-.1-1.4-.4-2.1-.7.3-.7.6-1.5.9-2.1.4.2.8.3 1.2.4 0 .1.1.1.2.1Zm-.1-2c.5.4.9.9 1.3 1.4-.2.1-.5.1-.8.1-.1-.6-.3-1.1-.5-1.5Zm-1.6.9c-.3-.2-.7-.4-1-.6.5-.3 1.1-.6 1.6-.7l-.6 1.3Zm-.3.5-1 2.1C10.8 7 10 6.5 9.3 6c.7-.6 1.3-1 2-1.5.5.3.9.6 1.3.8ZM9 5.6C8.4 5 8 4.5 7.8 3.9c.8-.3 1.7-.5 2.6-.7.1.3.3.6.6.9-.6.4-1.3.9-2 1.5Zm1.2-2.9c-.9.1-1.8.3-2.6.6-.1-.5 0-.9.2-1.3.8-.1 1.6-.1 2.4 0v.7ZM8.6 6c-.7.6-1.3 1.2-1.9 1.9A7.1 7.1 0 0 1 5 5.5c.7-.5 1.5-.9 2.3-1.3.2.5.7 1.2 1.3 1.8ZM7.1 3.6c-.8.3-1.6.8-2.3 1.2-.2-.7 0-1.4.4-1.9.6-.3 1.3-.6 2-.8-.2.5-.2 1-.1 1.5Zm-.8 4.7c-.6.7-1.1 1.4-1.5 2.1-1-.9-1.7-1.9-2-2.9.5-.6 1-1.2 1.7-1.8.3.9 1 1.8 1.8 2.6Zm-1.4 3c1.1.9 2.4 1.8 3.8 2.4l-1 2.1c-1.4-.7-2.7-1.5-3.8-2.4.1-.3.3-.7.4-1.1.3-.3.4-.6.6-1Zm3.2 5.3c1.2.5 2.4.9 3.5 1.1.3.1.6.1.9.1-.3.5-.7.9-1.1 1.2-.3 0-.7-.1-1-.1-1-.2-2-.5-3-.9l.7-1.4Zm3.5 3h.9c-.4.1-.9.2-1.3.2.2 0 .3-.1.4-.2Zm.6-.5c.3-.3.7-.7 1-1.2 1.1.1 2.2 0 3-.2-.4.4-.8.7-1.2 1-.8.3-1.7.5-2.8.4Zm.6-1.7c-.4 0-.7-.1-1.1-.2-1.1-.2-2.3-.6-3.4-1.1.1-.3.3-.6.5-1l.5-1.1c1.2.5 2.4.9 3.5 1.1.3.1.7.1 1 .2-.1.4-.3.7-.4 1.1-.2.3-.4.6-.6 1Zm.2-2.9c-1.1-.2-2.3-.6-3.4-1.1.4-.8.7-1.6 1.1-2.4 1 .4 2.1.8 3.1 1 .3.1.6.1.9.1-.1.9-.3 1.7-.6 2.5-.4 0-.8 0-1.1-.1Zm2.2-2.3c1.1.1 2.2 0 3-.4 0 .8 0 1.7-.2 2.4-.9.4-2 .5-3.4.5.3-.8.4-1.6.6-2.5Zm.1-.6c.1-.9.2-1.8.1-2.6.8 0 1.6 0 2.2-.3.3.8.5 1.7.5 2.5-.7.4-1.7.5-2.8.4Zm2.1-3.4c-.5.2-1.2.3-2 .2 0-.9-.1-1.7-.3-2.5.4 0 .7 0 1-.1.6.7 1 1.5 1.3 2.4Zm-1.1-3.1c-.3-.4-.7-.8-1.1-1.2.5.3.9.5 1.3.9 0 .1-.1.2-.2.3Zm-1-1.7c.4.1.7.2 1 .3.1.2.2.3.2.5-.4-.3-.8-.6-1.2-.8Zm-.7-.6c.2-.1.4-.2.5-.2.2.1.3.2.5.4-.3-.1-.6-.1-1-.2Zm-.5-.2c0-.2-.1-.5-.1-.7.2.1.5.2.7.3-.2.2-.4.3-.6.4Zm.4 2.7h-.1l-1-.3c.2-.5.4-1 .6-1.4.1.5.3 1.1.5 1.7Zm-1.8-3.6c.1 0 .2 0 .3.1.1 0 .2 0 .3.1.1.1.2.3.2.6-.2-.4-.5-.6-.8-.8Zm.2 1.4c-.5.2-.9.4-1.4.7-.2-.2-.4-.5-.5-.7h1.9Zm-.4-.4h-1.7c0-.2 0-.3.1-.5.5.1 1.1.3 1.6.5Zm-1.2-.9c.1-.1.3-.1.4-.1.3.1.6.3.9.5-.4-.1-.8-.3-1.3-.4Zm-.7-.1c-.5-.1-1-.1-1.5-.1h-.5c.3-.2.7-.3 1.1-.4.5 0 .9 0 1.4.2-.2.1-.4.2-.5.3ZM4.3 5.2c-.6.5-1.2 1.1-1.7 1.7-.1-.6-.1-1.1.1-1.5.4-.7 1-1.3 1.6-1.8-.2.5-.2 1 0 1.6Zm2.9 13.3c1 .4 2 .8 3.1.9.2 0 .3.1.5.1-.3.1-.5.2-.8.2-.3 0-.5-.1-.8-.1-.7-.1-1.5-.4-2.2-.7.1-.1.1-.2.2-.4Zm6.3-1.1c.2-.3.3-.6.5-.9.2-.4.3-.8.5-1.2 1.3.1 2.4 0 3.4-.4l-.3.9c-.2.4-.4.8-.6 1.1-1.1.4-2.2.6-3.5.5Zm4.4-1.4c.2-.4.3-.8.5-1.3.2-.1.5-.3.7-.4-.3.7-.7 1.3-1.1 1.9l-.2.2c0-.2.1-.3.1-.4Zm.7-2c.1-.8.2-1.5.1-2.3a4 4 0 0 0 1.2-1v.3c0 .6-.1 1.3-.3 1.8-.2.4-.5.8-1 1.2Z" />
      </svg>
    );
  }
  if (url.host == "demonstrations.wolfram.com") {
    icon = (
      <svg viewBox="0 0 100 100" className="externalLinkIcon">
        <path d="m50 0 10.5 17.75L79.75 9.5 77.5 30.25 98 34.5 84.25 50 98 65.5l-20.5 4.25 2.25 20.75-19.25-8.25L50 100 39.5 82.25 20.25 90.5l2.25-20.75L2 65.5 15.75 50 2 34.5l20.5-4.25L20.25 9.5l19.25 8.25Zm2 56.5c1.77-.55 6-4.15 6-6 0-5.35-8.5-5.5-8.5-5.5s4.25 1.86 4.25 6.5c0 3.09-1.75 5-1.75 5zm-4-13c-1.77.55-6 4.15-6 6 0 5.35 8.5 5.5 8.5 5.5s-4.25-1.86-4.25-6.5c0-3.09 1.75-5 1.75-5zM60 27s-7.01-4-14-4-21 5.9-21 23.5C25 65.52 44 65 44 65s-9.17-3.98-9-16c.3-20.97 25-22 25-22ZM36 52c-.6 3.39 4.53 13 11 13 7.47 0 11-13 11-13s-3.63 6-10.5 6C40.62 58 36 52 36 52Zm28-4c.6-3.39-4.53-13-11-13-7.47 0-11 13-11 13s3.63-6 10.5-6c6.88 0 11.5 6 11.5 6zM40 73s7.01 4 14 4 21-5.9 21-23.5C75 34.48 56 35 56 35s9.17 3.98 9 16c-.3 20.97-25 22-25 22z" />
      </svg>
    );
  }
  if (url.host == "datarepository.wolframcloud.com") {
    icon = (
      <svg viewBox="0 0 16 16" className="externalLinkIcon">
        <path d="m8 0a8 8 0 0 0 -8 8 8 8 0 0 0 8 8 8 8 0 0 0 8-8 8 8 0 0 0 -8-8zm.03 1.54 6 2.9v.56h-12v-.56zm.02.67-4.72 2.26h9.4zm-4.96 3.06h1.87v.53h-1.87zm8 0h1.87v.53h-1.87zm-7.46.8h.8v5.33h-.8zm2.66 0h.8v5.33h-.8zm5.34 0h.8v5.33h-.8zm-2.67 1.07h.8v4.26h-.8zm-4 .53h.8v3.73h-.8zm5.33.8h.8v2.93h-.8zm-2.66.8h.8v2.13h-.8zm-4.54 2.4h9.87v.53h-9.87zm-.8.8h11.47v.53h-11.47z" />
      </svg>
    );
  }
  if (url.host == "notebookarchive.org") {
    icon = (
      <svg viewBox="0 0 16 16" className="externalLinkIcon">
        <path d="M2 2H1V1h1.14c.26-.59.84-1 1.53-1h9.66C14.25 0 15 .75 15 1.67v12.66c0 .92-.75 1.67-1.67 1.67H3.67C2.75 16 2 15.25 2 14.33V14H1v-1h1v-1H1v-1h1v-1H1V9h1V8H1V7h1V6H1V5h1V4H1V3h1V2Zm1-.33v12.66c0 .37.3.67.67.67h9.66c.37 0 .67-.3.67-.67V1.67c0-.37-.3-.67-.67-.67H3.67C3.3 1 3 1.3 3 1.67Z" />
        <path d="m8.5 12.65-.99-1.66-1.8.77.2-1.91L4 9.44 5.3 8 4 6.56l1.91-.41-.2-1.92 1.8.78.99-1.66.99 1.66 1.8-.77-.2 1.91 1.91.41L11.7 8 13 9.44l-1.91.4.2 1.92-1.8-.78-.99 1.67Zm-.66-2.02.43.72v-.72l-.44-.64v.64Zm.9 0v.73l.43-.72.01-.65-.45.64Zm.89-.08 1.11.48-.12-1.2-.97-.3-.02 1.02Zm-3.25-.7-.12 1.18 1.11-.48-.02-1-.97.3Zm1.47-.61.65.92.65-.92-.65-.86-.65.86Zm2.37 0 .62.2.86-.2-.7-.23-.78.23Zm-4.92.01.86.19.62-.2-.77-.22-.7.23Zm3.58-1.14.64.86 1.1-.32-.7-.89-1.04.35Zm-2.5.54 1.1.32.65-.86-1.05-.35-.7.89Zm4.76-.07.66.22-.55-.6-.57-.21.46.6Zm-5.4-.39-.54.61.66-.22.46-.6-.57.21Zm4.62-.8.96.34.8-.89-1.19-.25-.57.8Zm-5.49-.55.8.9.96-.36-.56-.8-1.2.26Zm3.86-.26v1.1l1.05-.35.03-1.12-1.08.37Zm-1.5.75 1.04.35v-1.1L7.2 6.21l.03 1.12Zm3.05-1.37-.02.79.36-.5.08-.85-.42.56Zm-3.9.29.37.5-.02-.8-.43-.55.09.85Zm1.5-.97.62.77.61-.77-.61-1.03-.61 1.03Zm-.78.41.73.25-.36-.46-.77-.33.4.54Zm2.44-.2-.36.45.73-.25.4-.53-.77.33Z" />
      </svg>
    );
  }
  if (url.host == "resources.wolframcloud.com") {
    if (url.pathname.startsWith("/NeuralNetRepository/")) {
      icon = (
        <svg viewBox="0 0 16 16" className="externalLinkIcon">
          <path d="M8 0a8 8 0 00-8 8 8 8 0 008 8 8 8 0 008-8 8 8 0 00-8-8zm.77 1.67a.9.9 0 01.9.9.9.9 0 01-.22.58l3.64 2.6a.9.9 0 01.82-.54.9.9 0 01.9.9.9.9 0 01-1.4.73l-.85 1.22.85 1.22a.9.9 0 01.5-.15.9.9 0 01.9.89.9.9 0 01-1.72.35l-3.55 2.54a.9.9 0 01.13.46.9.9 0 11-1.77-.18l-4.32-1.3a.9.9 0 11-.88-1 .9.9 0 01.49.16L4.28 9.4l-.88-.8a.9.9 0 11-.7-1.44.9.9 0 01.7.34l.88-.8-1.1-1.63a.9.9 0 11-.48-1.64.9.9 0 01.88.8l4.37-1.32a.9.9 0 01.82-1.25zM8 3.01L3.59 4.34a.9.9 0 01-.06.31l2.28.69L8.16 3.2a.9.9 0 01-.16-.2zm1.38.21a.9.9 0 01-.13.1l2.03 2.91h1.75a.9.9 0 01.02-.38zm-1.15.06l-2.31 2.1.86.25 1.53-2.3a.9.9 0 01-.08-.05zm.93.09a.9.9 0 01-.76 0L6.88 5.67l1.06.32a.9.9 0 01.83-.58.9.9 0 01.9.83h1.49zM3.49 4.74a.9.9 0 01-.1.16l1.44 1.31.89-.8zm-.17.24a.9.9 0 01-.05.04l1.08 1.63.41-.37zm2.51.47l-.92.83.9.82.91-1.38zm1 .3l-.9 1.33L7.9 6.5a.9.9 0 010-.42zm2.83.58a.9.9 0 01-.13.43L11.26 8l.75-.54-.78-1.13zm1.7 0l.74 1.07 1.08-.78a.9.9 0 01-.13-.29zm-6.53.02l-.42.39.45.67.86-.26zm3.1.24l-2.01.6.88.8 1.27-1.14a.9.9 0 01-.14-.26zm5.31.1l-1.09.79.35.5.83-1.2a.9.9 0 01-.09-.08zm-8.9.11l-.88.8a.9.9 0 01.1.2l1.2-.36zm5.13.05a.9.9 0 01-1.33.07L6.88 8.06 8.14 9.2a.9.9 0 01.63-.26.9.9 0 01.7.34l1.7-1.22zm-3.64.4l-.54.81.54.81.9-.8zm-.13.01l-.79.24.32.47zm-.89.27l-1.23.37a.9.9 0 01.01.16.9.9 0 01-.02.16l1.24.38.36-.54zm7.26 0l-.73.53.73.53.37-.53zm-.81.6L9.53 9.35a.9.9 0 01.13.43h1.57l.79-1.12zm-4.46 0l-.88.8 2.01.6a.9.9 0 01.14-.25zm5.7.02l-.35.5 1.1.77a.9.9 0 01.08-.08zm-7.27 0l-.32.48.8.23zm-1.68.17a.9.9 0 01-.1.2l.88.8.43-.64zm1.3.4l-.44.67.42.38.89-.8zm7.25 0l-.75 1.07h1.7a.9.9 0 01.13-.29zm-6.3.3l-.9.82.93.84.9-.27zm.14.02l.88 1.34 1.09-.33a.9.9 0 01-.01-.42zm-1.59.43L3.27 11.1a.9.9 0 01.05.05l1.44-1.3zm5.31.42a.9.9 0 01-1.72.25l-1.06.32 1.43 2.15a.9.9 0 01.46-.13.9.9 0 01.49.14l1.9-2.73zm1.62 0l-1.94 2.79a.9.9 0 01.14.15l3.57-2.56a.9.9 0 01-.02-.38zm-6.45.02l-1.45 1.3a.9.9 0 01.11.17l2.23-.67zm1.95.58l-.86.26 2.2 2a.9.9 0 01.1-.08zm-.97.3l-2.28.68a.9.9 0 01.06.32l4.33 1.3a.9.9 0 01.14-.26z" />
        </svg>
      );
    }
    if (url.pathname.startsWith("/FunctionRepository/")) {
      icon = (
        <svg viewBox="0 0 60 60" className="externalLinkIcon">
          <path d="M30 0a30.01 30.01 0 0 1 0 60 30.01 30.01 0 0 1 0-60Zm2.77 35v.01c0 .6.5 1.11 1.11 1.11h5.78c.6 0 1.11-.5 1.11-1.11v-5.78c0-.6-.5-1.11-1.11-1.11h-5.78c-.6 0-1.11.5-1.11 1.11V35Zm9 6.01 6.38.01v-17.8h-6.38v1.43c3.14.31 3.28.38 3.28 3.1v8.73c0 2.72-.14 2.8-3.28 3.1v1.43Zm-16.37 0h6.37v-1.43c-3.2-.31-3.27-.38-3.27-2.96v-8.91c0-2.72.07-2.79 3.27-3.1v-1.4H25.4v17.8ZM13.13 25.52l-1.25.01-1.04 1.36.2.41h2.09v9.96c0 2.09-.17 2.23-1.94 2.47v1.3h8.9v-1.3c-2.36-.24-2.6-.37-2.6-2.47V27.3h3.2c.49-.27.59-1.39.45-1.77h-3.65l-.04-.77a22.54 22.54 0 0 1 0-4.66c.14-.56.38-.94.98-.94.91 0 1.88.87 2.71 1.7a.76.76 0 0 0 1.08.03c.55-.4.95-.96 1.15-1.6a1.4 1.4 0 0 0-.49-1.35A3.81 3.81 0 0 0 20.2 17c-1.48.2-2.85.87-3.93 1.9a8.56 8.56 0 0 0-3.14 6.02v.6Z" />
        </svg>
      );
    }
    if (url.pathname.startsWith("/PacletRepository/")) {
      icon = (
        <svg viewBox="0 0 16 16" className="externalLinkIcon">
          <path d="M8.2 15.47V8.11l1.59-.92 4.78-2.76v7.36ZM1.42 4.43 6.21 7.2l1.6.92v7.36L1.43 11.8ZM8 .42l6.37 3.68L9.6 6.86 8 7.78l-1.6-.92L1.64 4.1Zm6.86 3.5L8.1.03a.2.2 0 0 0-.2 0l-6.76 3.9a.2.2 0 0 0-.1.18v7.8c0 .07.04.14.1.17l6.76 3.9a.2.2 0 0 0 .2 0l6.76-3.9a.2.2 0 0 0 .1-.17V4.1a.2.2 0 0 0-.1-.17" />
          <path d="m1.98 5.38 5.28 3.05v6.1l-5.28-3.05ZM14.02 5.38v6.1l-5.28 3.05v-6.1ZM8 1.05l5.28 3.05L8 7.15 2.72 4.1Z" />
        </svg>
      );
    }
    if (url.pathname.startsWith("/PromptRepository/")) {
      icon = (
        <svg viewBox="0 0 96 96" className="externalLinkIcon">
          <path d="M41 73 23 94V73H7.1A7.1 7.1 0 0 1 0 65.9V9.1A7.1 7.1 0 0 1 7.1 2h81.8A7.1 7.1 0 0 1 96 9.1v56.8a7.1 7.1 0 0 1-7.1 7.1H41ZM22.5 48.5v5.9a5.1 5.1 0 0 0 5.1 5.1h40.8a5.1 5.1 0 0 0 5.1-5.1v-5.9h4a3 3 0 0 0 3-3v-12a3 3 0 0 0-3-3h-4v-4.9a5.1 5.1 0 0 0-5.1-5.1H49.5v-2.2a5.5 5.5 0 0 0 4-5.3A5.5 5.5 0 0 0 48 7.5a5.5 5.5 0 0 0-5.5 5.5 5.5 5.5 0 0 0 4 5.3v2.2H27.6a5.1 5.1 0 0 0-5.1 5.1v4.9h-4a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3h4Zm25.5-38a2.5 2.5 0 0 1 0 5 2.5 2.5 0 0 1 0-5Zm-29.5 23h4v12h-4v-12Zm52 20.9a2.1 2.1 0 0 1-2.1 2.1H27.6a2.1 2.1 0 0 1-2.1-2.1V25.6c0-1.16.95-2.1 2.1-2.1h40.8c1.15 0 2.1.94 2.1 2.1v28.8ZM37.96 37.28s.31-1.78 2.03-1.78c1.72 0 2.03 1.78 2.03 1.78a1.5 1.5 0 0 0 2.94-.56S44.15 32.5 40 32.5s-4.97 4.22-4.97 4.22a1.5 1.5 0 0 0 2.94.56ZM40.1 49.7s3 2.3 7.9 2.3 7.9-2.3 7.9-2.3a1.5 1.5 0 0 0-1.8-2.4S51.77 49 48 49c-3.77 0-6.1-1.7-6.1-1.7a1.5 1.5 0 0 0-1.8 2.4Zm13.87-12.42s.31-1.78 2.03-1.78c1.72 0 2.03 1.78 2.03 1.78a1.5 1.5 0 0 0 2.94-.56S60.15 32.5 56 32.5s-4.97 4.22-4.97 4.22a1.5 1.5 0 0 0 2.94.56ZM73.5 45.5v-12h4.01v12h-4Z" />
        </svg>
      );
    }
  }
  return (
    <li>
      <a href={url}>
        {icon} <span className="text">{props.text}&nbsp;&raquo;</span>
      </a>
    </li>
  );
}
function ExternalLinksSection(props) {
  const links = props.links.map((link) => (
    <ExternalLink key={link.URL} url={link.URL} text={link.Text} />
  ));
  return (
    <>
      <h2>{props.headerText}</h2>
      <ul id="external-links">{links}</ul>
      <hr />
    </>
  );
}
function SnapshotsSection(props) {
  const triggers = props.snapshots.map((snapshot, index) => {
    return (
      <Tabs.Trigger
        key={snapshot}
        className="SnapshotsTabsTrigger"
        value={"tab" + index}
      >
        <img src={snapshot} alt="" />
      </Tabs.Trigger>
    );
  });
  const content = props.snapshots.map((snapshot, index) => {
    return (
      <Tabs.Content
        key={snapshot}
        className="SnapshotsTabsContent"
        value={"tab" + index}
      >
        <img src={snapshot} alt="" />
      </Tabs.Content>
    );
  });
  return (
    <>
      <h2>Snapshots</h2>
      <Tabs.Root className="SnapshotsTabsRoot">
        <Tabs.List className="SnapshotsTabsList" aria-label="Choose a snapshot">
          {triggers}
        </Tabs.List>
        {content}
      </Tabs.Root>
      <hr />
    </>
  );
}
function MoreTopicsSection(props) {
  const topicLinks = props.topics.map((topic) => (
    <li key={topic.ID}>
      <Link to={"../topic/" + topic.ID} reloadDocument={true}>
        {topic.Name}
      </Link>
    </li>
  ));
  const topicsListClassName =
    "topics-list " + (topicLinks.length > 3 ? "long" : "short");
  return (
    <div className="related-topics">
      <p className="sidebar-heading">Related Topics</p>
      <ul className={topicsListClassName}>
        {topicLinks}
        <li>
          <Link to="../topics/">Browse all topics</Link>
        </li>
      </ul>
    </div>
  );
}

function formatDate(date) {
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
  });
}

function CitationSection(props) {
  const authorsText = props.citation.Authors
    ? props.citation.Authors.map(
        (author, index) =>
          (index ? ", " : "") +
          (author.Label ? author.Label + " " : "") +
          author.Name +
          (author.Suffix ? " " + author.Suffix : ""),
      ).join("")
    : "";
  const dateText = props.citation.Date ? formatDate(props.citation.Date) : "";
  const citationText = `${authorsText} (${dateText}), "${props.title}" Wolfram Demonstrations Project. demonstrations.wolfram.com/${props.id}/`;

  const citationContents = (
    <>
      {props.citation.Authors && (
        <ListOfAuthors authors={props.citation.Authors} />
      )}{" "}
      ({props.citation.Date && <>{formatDate(props.citation.Date)}</>}), "
      {props.title}" Wolfram Demonstrations Project. demonstrations.wolfram.com/
      <wbr />
      {props.id}/
    </>
  );

  return (
    <>
      <h2>Permanent Citation</h2>
      <div id="cite-box">
        <p>Cite this as</p>
        <p>
          <ClickToCopy text={citationText}>
            <button className="citationButton">{citationContents}</button>
          </ClickToCopy>
        </p>
      </div>
    </>
  );
}

function NonCloudSlideshow(props) {
  const { snapshots, downloadURL } = props;
  const [activeIndex, setActiveIndex] = useState(0);
  const snapshotCount = snapshots ? snapshots.length : 0;
  const hasDownloadURL =
    typeof downloadURL === "string" && downloadURL.length > 0;
  const downloadTile = (
    <>
      <p className="title">This demonstration is optimized for desktop</p>
      <p className="link-frame">
        {hasDownloadURL ? (
          <a href={downloadURL}>
            <svg viewBox="0 0 30 30" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M16 4h11.16A2.84 2.84 0 0 1 30 6.84v16.32A2.84 2.84 0 0 1 27.16 26H20v2h3.5v2h-17v-2H10v-2H2.84A2.84 2.84 0 0 1 0 23.16V6.84A2.84 2.84 0 0 1 2.84 4H14V.5h2V4Zm-2 2H2.84a.84.84 0 0 0-.84.84v16.32c0 .46.38.84.84.84h24.32c.46 0 .84-.38.84-.84V6.84a.84.84 0 0 0-.84-.84H16v10.59l2.3-2.3a1 1 0 0 1 1.4 1.42l-4 4a1 1 0 0 1-1.4 0l-4-4a1 1 0 0 1 1.4-1.42l2.3 2.3V6Z"
                fill-opacity=".5"
              />
            </svg>
            <span>Download to Desktop</span>
          </a>
        ) : (
          <span>Download unavailable</span>
        )}
      </p>
    </>
  );

  useEffect(() => {
    if (snapshotCount === 0) {
      setActiveIndex(0);
      return;
    }

    setActiveIndex((prev) => (prev >= snapshotCount ? 0 : prev));

    if (snapshotCount <= 1) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % snapshotCount);
    }, 4000);

    return () => clearInterval(interval);
  }, [snapshotCount]);

  if (!snapshots || snapshots.length === 0) {
    return (
      <div
        className="noncloud-placeholder"
        role="region"
        aria-label="Demonstration preview unavailable"
      >
        {downloadTile}
      </div>
    );
  }

  return (
    <div className="noncloud-slideshow-wrapper">
      <div className="noncloud-slideshow">
        {snapshots.map((src, index) => (
          <img
            key={src}
            src={src}
            alt=""
            className={
              "noncloud-slideshow-slide" +
              (index === activeIndex ? " active" : "")
            }
          />
        ))}
      </div>
      <div className="ddoverlay-frame">
        <div className="ddoverlay-tile">{downloadTile}</div>
      </div>
    </div>
  );
}

function FullScreenModal(props) {
  const manipulateRef = useRef(null);
  const [manipulateLoading, setManipulateLoading] = useState(true);

  useEffect(() => {
    function moveAndResizeManipulate() {
      function doMoveAndResize() {
        manipulateRef.current.style.transform = "unset";

        const manipulateSize = manipulateRef.current
          .querySelector(".TagBoxContainer")
          .getBoundingClientRect();
        const manipulateSizeFudge = 2;
        manipulateRef.current.style.position = "absolute";
        manipulateRef.current.style.height =
          manipulateSize.height + manipulateSizeFudge + "px";
        manipulateRef.current.style.width =
          manipulateSize.width + manipulateSizeFudge + "px";
        manipulateRef.current.style.top =
          window.innerHeight / 2 -
          (manipulateSize.height + manipulateSizeFudge) / 2 +
          "px";
        manipulateRef.current.style.left =
          window.innerWidth / 2 -
          (manipulateSize.width + manipulateSizeFudge) / 2 +
          "px";
        manipulateRef.current.style.transform =
          "scale(" +
          (Math.floor(
            Math.min(
              window.innerHeight / manipulateSize.height,
              window.innerWidth / manipulateSize.width,
            ) * 10,
          ) /
            10 -
            0.2) +
          ")";
      }
      window.setTimeout(doMoveAndResize, 20);
      window.setTimeout(doMoveAndResize, 400);
    }
    window.manRef = manipulateRef.current;

    WolframNotebookEmbedder.embed(
      props.manipulateInfo.URL,
      manipulateRef.current,
      { showRenderProgress: false },
    ).then((embedded) => {
      embedded.addEventListener("initial-render-done", () => {
        manipulateRef.current.style.opacity = 1;
        setManipulateLoading(false);

        moveAndResizeManipulate();
      });
      window.addEventListener("click", moveAndResizeManipulate);
      window.addEventListener("resize", moveAndResizeManipulate);
      return () => {
        window.removeEventListener("click", moveAndResizeManipulate);
        window.removeEventListener("resize", moveAndResizeManipulate);
      };
    });
  }, [props.manipulateInfo]);
  return (
    <div id="fullScreenModal">
      <button
        onClick={() => {
          props.closeAction();
        }}
        className="close-button"
      >
        <svg viewBox="0 0 16 16">
          <title>Close</title>
          <path d="M2,2L14,14" />
          <path d="M14,2L2,14" />
        </svg>
      </button>
      {manipulateLoading && <LoadingSpinner />}
      <div className="cloudembed section">
        <div ref={manipulateRef} style={{ opacity: 0 }} />
      </div>
    </div>
  );
}

function SidebarContents(props) {
  return (
    <div className="sidebar">
      <SidebarTabs
        relatedLabel={props.pageData.RelatedDemonstrations.Label}
        relatedSuffix={props.pageData.RelatedDemonstrations.Suffix}
        relatedURL={props.pageData.RelatedDemonstrations.URL}
        authorLabel={props.pageData.MoreByAuthor.Label}
        authorSuffix={props.pageData.MoreByAuthor.Suffix}
        authorURL={props.pageData.MoreByAuthor.URL}
      />
      {props.pageData.Categories.Categories && (
        <MoreTopicsSection topics={props.pageData.Categories.Categories} />
      )}
    </div>
  );
}

export default function DemonstrationPage() {
  const pageData = useLoaderData();

  const [showFullScreenModal, setFullScreenModal] = useState(false);
  function openFullScreenModal(e) {
    setFullScreenModal(true);
  }
  function closeFullScreenModal(e) {
    setFullScreenModal(false);
  }

  const manipulateRef = useRef(null);
  const loadingManipulateRef = useRef(null);
  const captionRef = useRef(null);
  const detailsRef = useRef(null);
  const referencesRef = useRef(null);
  useEffect(() => {
    if (pageData.CloudCompatible) {
      const cacheurl = pageData.Manipulate.URL.replace(
        /\/(obj|objects)\//,
        "/statichtml/",
      );
      fetch(cacheurl, { method: "GET" })
        .then((response) => response.text())
        .then((text) => {
          if (manipulateRef.current) {
            manipulateRef.current.innerHTML = text;
          }
        })
        .catch(() => {
          if (manipulateRef.current) {
            manipulateRef.current.innerHTML = "";
          }
        });

      const loadingLiveNb = loadingManipulateRef.current;
      if (loadingLiveNb) {
        loadingLiveNb.setAttribute("data-show", "true");
      }

      WolframNotebookEmbedder.embed(
        pageData.Manipulate.URL,
        manipulateRef.current,
        { showRenderProgress: false },
      ).then((embedded) => {
        embedded.addEventListener("initial-render-done", () => {
          if (loadingLiveNb) {
            loadingLiveNb.setAttribute("data-show", "false");
          }
        });
      });
    }

    pageData.Caption &&
      WolframNotebookEmbedder.embed(
        pageData.Caption.URL,
        captionRef.current,
        typeof pageData.Caption.Width === "undefined" &&
          pageData.Caption.Height === "undefined"
          ? { showRenderProgress: false, allowInteract: false }
          : {
              width: pageData.Caption.Width,
              maxHeight: pageData.Caption.Height,
              showRenderProgress: false,
              allowInteract: false,
            },
      );
    pageData.Details &&
      WolframNotebookEmbedder.embed(
        pageData.Details.URL,
        detailsRef.current,
        typeof pageData.Details.Width === "undefined" &&
          pageData.Details.Height === "undefined"
          ? { showRenderProgress: false, allowInteract: false }
          : {
              width: pageData.Details.Width,
              maxHeight: pageData.Details.Height,
              showRenderProgress: false,
              allowInteract: false,
            },
      );
    pageData.References &&
      WolframNotebookEmbedder.embed(
        pageData.References.URL,
        referencesRef.current,
        typeof pageData.References.Width === "undefined" &&
          pageData.References.Height === "undefined"
          ? { showRenderProgress: false, allowInteract: false }
          : {
              width: pageData.References.Width,
              maxHeight: pageData.References.Height,
              showRenderProgress: false,
              allowInteract: false,
            },
      );
  }, []);

  return (
    <main
      className={
        showFullScreenModal
          ? "main demonstration modalOpen"
          : "main demonstration"
      }
    >
      <Helmet>
        <title>{pageData.Title} | Wolfram Demonstrations Project</title>
      </Helmet>
      {showFullScreenModal && (
        <FullScreenModal
          manipulateInfo={pageData.Manipulate}
          closeAction={closeFullScreenModal}
        />
      )}
      <section>
        <div className="inner">
          <PageTitle title={pageData.Title} />
          {pageData.ResourceObjectButton && (
            <ResourceObjectSection
              content={pageData.ResourceObjectButton.Content}
            />
          )}
          <div className="content">
            <div className="left">
              <div className="object">
                {pageData.CloudCompatible ? (
                  <>
                    <div className="cloudembed">
                      <div ref={manipulateRef} />
                    </div>
                    <div
                      id="loading-manipulate"
                      ref={loadingManipulateRef}
                      data-show="false"
                    >
                      Initializing live version
                    </div>
                  </>
                ) : (
                  <div className="cloudembed">
                    <NonCloudSlideshow
                      snapshots={pageData.Snapshots}
                      downloadURL={
                        pageData.SourceNotebookButton
                          ? pageData.SourceNotebookButton.URL
                          : null
                      }
                    />
                  </div>
                )}
                <ButtonsUnderDemonstration
                  pageData={pageData}
                  openFullScreenModal={openFullScreenModal}
                />
              </div>
              {pageData.Caption && (
                <div ref={captionRef} style={{ margin: ".5rem 0 2rem 0" }} />
              )}
              {pageData.AuthorInformation && (
                <ContributedBySection
                  label={pageData.AuthorInformation.Label}
                  suffix={pageData.AuthorInformation.Suffix}
                  authors={pageData.AuthorInformation.Authors}
                  comment={pageData.AuthorInformation.Comment}
                  date={formatDate(pageData.PublishedDate)}
                />
              )}
              <hr />
              {pageData.Snapshots && (
                <SnapshotsSection snapshots={pageData.Snapshots} />
              )}
              {pageData.Details && (
                <>
                  <h2>Details</h2>
                  <div ref={detailsRef} />
                  <hr />
                </>
              )}
              {pageData.ExternalLinks && (
                <ExternalLinksSection
                  headerText={pageData.ExternalLinks.Label}
                  links={pageData.ExternalLinks.Links}
                />
              )}
              {pageData.Citation && (
                <CitationSection
                  citation={pageData.Citation}
                  title={pageData.Title}
                  id={pageData.ID}
                />
              )}
              {pageData.References && (
                <>
                  <h2>References</h2>
                  <div ref={referencesRef} />
                </>
              )}
            </div>
            <Desktop>
              <SidebarContents pageData={pageData} />
            </Desktop>
          </div>

          {/* <pre style={{ font: ".5em monospace", marginTop: 100 }}>
            {JSON.stringify(pageData, null, 2)}
          </pre> */}
        </div>
      </section>
      <NotDesktop>
        <section className="mobile-sidebar">
          <div className="inner">
            <SidebarContents pageData={pageData} />
          </div>
        </section>
      </NotDesktop>
    </main>
  );
}
